import { createContext, useContext, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Nav, Row, Tab } from "react-bootstrap";
import {
  CANCEL,
  SAVE,
  PATIENT_SAVE_FAIL,
  SavingPatient,
  PATIENT_CREATE,
  PATIENT_UPDATE,
} from "../../util/const";
import BglSettings from "../BglSettings";
import PatientDetails from "../PatientDetails";
import { StepOneProps } from "../CreatePatient";
import { PatientContext } from "../ActivePatients";
import LoadingSpinner from "../../Components/LoadingSpinner";
import { DatabaseManager } from "../../services/DatabaseManager";
import PatientReviewFragment from "../PatientReviewFragment";
import AlertSettingsDisplay from "../AlertSettingsDisplay";


export const ValidationContext = createContext({
  hasValidated: false,
});

const PatientForm = (props: StepOneProps) => 
{
  const context = useContext(PatientContext);

  const detailsRef = useRef<HTMLFormElement>(null);
  const bglRef = useRef<HTMLFormElement>(null);

  var validation = useRef(false);
  const [hasValidated, setHasValidated] = useState(validation.current);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const validationContextValue = useMemo(() => ({ hasValidated }), [hasValidated]);

  const cancel = () => {
    props.toggleDisplay(false);
  };

  const save = () => {
    validation.current = true;
    setHasValidated(validation.current);

    var bglValid = false;
    if (bglRef.current) {
      bglRef.current.checkValidity();
      bglValid = bglRef.current.reportValidity();
    }

    var detailsValid = false;
    if (detailsRef.current) {
      detailsRef.current.checkValidity();
      detailsValid = detailsRef.current.reportValidity();
    }

    if (bglValid && detailsValid) {
      if (props.isEditing) {
        DatabaseManager.getInstance()
          .put(PATIENT_UPDATE, context.patient)
          .then((response) => {
            props.toggleDisplay(true);
          })
          .then((response) => {})
          .catch(() => {
            setError(PATIENT_SAVE_FAIL);
          })
          .finally(() => {
            setIsSaving(false);
          });
      } else {
        DatabaseManager.getInstance()
          .post(PATIENT_CREATE, context.patient)
          .then((response) => {
            props.toggleDisplay(true);
          })
          .catch(() => {
            setError(PATIENT_SAVE_FAIL);
          })
          .finally(() => {
            setIsSaving(false);
          });
      }
    }
  };

  const header = (title: string) => {
    return (
      <Card.Header className="fs-5 app-colour white">
        <Row>
          <Col className="">{title}</Col>
          <Col className="right">
            <Button
              type="button"
              onClick={cancel}
              disabled={isSaving}
              className="fs-6 btn-outline-white me-2">
              {CANCEL}
            </Button>
            <Button
              type="submit"
              onClick={save}
              disabled={isSaving}
              className="fs-6 btn-outline-white">
              {SAVE}
            </Button>
          </Col>
        </Row>
      </Card.Header>
    );
  };

  const spinnerDiv = () => {
    return (
      <div>
        <LoadingSpinner></LoadingSpinner>
        <div className="text-style">{SavingPatient}</div>
      </div>
    );
  };

  if (props.isEditing) {
    return (
      <Card className="mt-3 mb-3" border="secondary">
        {header(
          `Edit Patient: ${context.patient.familyName!.toUpperCase()}, ${
            context.patient.givenNames
          }`
        )}
        {isSaving ? (
          spinnerDiv()
        ) : (
          <Card.Body>
            <div>{error}</div>
            <Tab.Container defaultActiveKey="data">
              <Nav variant="pills">
                <Nav.Item>
                  <Nav.Link eventKey="details" className="mb-3 color-text">
                    Details
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="data" className="mb-3 color-text">
                    Data
                  </Nav.Link>
                </Nav.Item>
              </Nav>
              <Tab.Content>
                <Tab.Pane eventKey="details">
                  <ValidationContext.Provider value={validationContextValue}>
                    <PatientDetails ref={detailsRef} />
                    <BglSettings ref={bglRef} />
                  </ValidationContext.Provider>
                  <AlertSettingsDisplay />
                </Tab.Pane>
                <Tab.Pane eventKey="data">
                  <PatientReviewFragment />
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </Card.Body>
        )}
      </Card>
    );
  }

  return (
    <Card className="mt-3 mb-3" border="secondary" text="white">
      {header(`${props.title}`)}
      {isSaving ? (
        spinnerDiv()
      ) : (
        <Card.Body className="color-text">
          <div>{error}</div>
          <ValidationContext.Provider value={validationContextValue}>
            <PatientDetails ref={detailsRef} />
            <BglSettings ref={bglRef} />
            <AlertSettingsDisplay />
          </ValidationContext.Provider>
        </Card.Body>
      )}
    </Card>
  );
};

export default PatientForm;